import { Injectable, Injector } from '@angular/core';
import { CommonPrinterService, OnDiscover, OnError } from '../common-printer.service';
import { StarPrintersScannerInterface } from '../../interfaces/star-printers-scanner.interface';
import { Subject, Observable } from 'rxjs';
import { ReceiptPrinter } from '../../classes/receipt-printer.class';
import { PrinterDto } from '../../dto/printer.dto';
import { StarPrinterPlugin, BarcodeReaderResultMessage } from '@paymash/capacitor-star-printer-plugin';
import { PrinterType } from '../../enum/printer-type.enum';
import {
  isStarBusyError,
  isStarConfirmedNotReadyReason,
  isStarPostWriteDisconnect,
  isStarPortOpenFailure,
  parseStarOutcome,
  PrinterBusyError,
  PrinterNotReadyError,
  PrinterRetryableError,
  PrinterUncertainError,
  STAR_POST_WRITE_UNCONFIRMED,
  STAR_PRE_WRITE_NO_CONNECTION,
  STAR_PRE_WRITE_NOT_READY,
  STAR_WRITE_INCOMPLETE,
  STAR_WRITE_NOTHING_SENT,
  StarPrintOutcome,
  starLegacyPostWriteReason,
} from '../../errors';

const STAR_POST_PRINT_COOLDOWN_MS = 1000;
const STAR_PORT_OPEN_MAX_RETRIES = 4;
const STAR_PORT_OPEN_BASE_DELAY_MS = 1000;
const STAR_IOS_RECLASSIFIED_MAX_TRIES = 3;

@Injectable()
export class StarPrinterService extends CommonPrinterService implements StarPrintersScannerInterface {
  private _scannerEvents: Subject<BarcodeReaderResultMessage> = new Subject<BarcodeReaderResultMessage>();

  constructor(injector: Injector) {
    super(injector);
  }

  protected get discoverMethodName(): string {
    return '';
  }

  protected get pluginName(): string {
    return 'StarPrinterService';
  }

  protected get printMethodName(): string {
    return '';
  }

  protected get stopDiscoverMethodName(): string {
    return '';
  }

  get scannerEvents(): Observable<BarcodeReaderResultMessage> {
    return this._scannerEvents.asObservable();
  }

  protected callDiscoverMethod(onDiscover: OnDiscover, onError: OnError) {
    StarPrinterPlugin.setDiscoverDevicesListener({
      onMessage: (data: any) => {
        if (data?.printers) {
          data.printers.forEach((printer) => (printer.deviceType = PrinterType.Star));
          onDiscover(data.printers);
        }
      },
      onError: (error) => onError(error),
    });
    StarPrinterPlugin.startDiscoverDevices().catch((error) => onError(error));
  }

  protected callStopDiscoverMethod(): Promise<void> {
    return StarPrinterPlugin.stopDiscoverDevices();
  }

  protected callPrintReceiptMethod(printer: PrinterDto, receipt: any[]): Promise<void> {
    // Boundary mapping: translate Star plugin errors into the right outcome so
    // the print queue can apply distinct policies:
    //   - port busy              → PrinterBusyError → backoff retry with jitter
    //                              (cross-device contention, no duplicates)
    //   - post-write disconnect  → SUCCESS. Wi-Fi Star (TSP143IIIW) drops the TCP
    //                              connection AFTER a successful write; the receipt
    //                              printed, so resolving avoids a false failure.
    //   - port-open failure      → propagate unchanged; the per-part retry in print()
    //                              re-attempts the connection (pre-write, safe).
    //   - everything else        → propagate unchanged; the queue's tries budget
    //                              handles paper/cover/offline cases.
    return StarPrinterPlugin.print({ printer, receipt })
      .catch((error) => {
          const message =
            typeof error === 'object' && error !== null && 'message' in error
              ? String((error as { message?: unknown }).message ?? '')
              : String(error);

          const outcome = parseStarOutcome(message);
          if (outcome) {
            const outcomeError = this.mapStarOutcome(outcome, message, error);
            if (!outcomeError) {
              this.logger.info('StarPrinterService:postWriteUnconfirmed:treatedAsSuccess', { message });
              return;
            }
            throw outcomeError;
          }

          if (isStarBusyError(error)) {
            throw new PrinterBusyError(PrinterType.Star, message, error);
          }
          if (isStarPostWriteDisconnect(error)) {
            this.logger.info('StarPrinterService:postWriteDisconnect:treatedAsSuccess', { message });
            return; // resolve: the data was written and printed before the drop
          }
          const legacyPostWriteReason = starLegacyPostWriteReason(error);
          if (legacyPostWriteReason) {
            throw new PrinterRetryableError(PrinterType.Star, legacyPostWriteReason, message, error);
          }
          throw error;
        })
      .then(() => new Promise<void>((resolve) => setTimeout(resolve, STAR_POST_PRINT_COOLDOWN_MS)));
  }

  private mapStarOutcome(outcome: StarPrintOutcome, message: string, error: unknown): Error | null {
    const isIos = !this.isAndroid;
    const reason = outcome.reason;
    switch (outcome.token) {
      case STAR_PRE_WRITE_NOT_READY:
        if (isStarConfirmedNotReadyReason(reason)) {
          return new PrinterRetryableError(PrinterType.Star, reason, message, error, isIos);
        }
        return new PrinterRetryableError(
          PrinterType.Star,
          reason ?? 'OFFLINE_OTHER',
          message,
          error,
          isIos,
          isIos ? STAR_IOS_RECLASSIFIED_MAX_TRIES : undefined
        );
      case STAR_PRE_WRITE_NO_CONNECTION:
        if (outcome.nativeError && isStarBusyError(outcome.nativeError)) {
          return new PrinterBusyError(PrinterType.Star, message, error);
        }
        return new PrinterRetryableError(
          PrinterType.Star,
          'NO_CONNECTION',
          message,
          error,
          isIos,
          isIos ? STAR_IOS_RECLASSIFIED_MAX_TRIES : undefined
        );
      case STAR_WRITE_NOTHING_SENT:
        return new PrinterRetryableError(PrinterType.Star, 'WRITE_FAILED', message, error);
      case STAR_WRITE_INCOMPLETE:
        return outcome.written === 0
          ? new PrinterRetryableError(PrinterType.Star, 'WRITE_FAILED', message, error)
          : new PrinterUncertainError(PrinterType.Star, message, error, 'WRITE_INCOMPLETE');
      case STAR_POST_WRITE_UNCONFIRMED:
        if (isStarConfirmedNotReadyReason(reason)) {
          return new PrinterRetryableError(PrinterType.Star, reason, message, error, isIos);
        }
        // iOS: a link drop, paper left in the presenter or a bare offline flag after the full write means printed; a reported fault does not.
        if (isIos && (reason === 'IO' || reason === 'PAPER_NOT_TAKEN' || reason === 'OFFLINE_OTHER')) {
          return null;
        }
        return new PrinterUncertainError(PrinterType.Star, message, error, reason ?? 'POST_WRITE', isIos);
      default:
        return new PrinterUncertainError(PrinterType.Star, message, error, 'UNKNOWN');
    }
  }

  /**
   * Prints one part (one connection) with in-place retry for "Failed to open a LAN port".
   * That is a pre-write failure (previous socket still in TIME_WAIT), so retrying the same
   * part's connection is safe. Retrying here — rather than letting the whole queue item fail
   * and re-print — avoids duplicating earlier cut-separated parts of a TSP143 receipt.
   *
   * On exhaustion the outcome depends on whether earlier parts already printed:
   *   - first part (partIndex 0): nothing printed yet → propagate the original error so the
   *     queue's normal tries budget can safely re-print the whole job.
   *   - later part: earlier parts are already on paper → surface PrinterUncertainError so the
   *     queue does NOT retry (which would duplicate them); the operator re-prints if needed.
   */
  private printPartWithPortRetry(printer: PrinterDto, receipt: any[], partIndex: number, attempt = 0): Promise<void> {
    return this.callPrintReceiptMethod(printer, receipt).catch((error) => {
      if (
        (error instanceof PrinterRetryableError || error instanceof PrinterNotReadyError) &&
        partIndex > 0 &&
        !isStarConfirmedNotReadyReason(error.reason)
      ) {
        throw new PrinterUncertainError(PrinterType.Star, error.originalMessage, error, error.reason, error.legacyIosSuccess);
      }
      if (
        error instanceof PrinterNotReadyError ||
        error instanceof PrinterRetryableError ||
        error instanceof PrinterUncertainError ||
        error instanceof PrinterBusyError
      ) {
        throw error; // already classified; its message must not reach the legacy text detectors
      }
      if (!isStarPortOpenFailure(error)) {
        throw error;
      }
      if (attempt < STAR_PORT_OPEN_MAX_RETRIES) {
        const delay = STAR_PORT_OPEN_BASE_DELAY_MS * (attempt + 1); // adaptive backoff
        this.logger.info('StarPrinterService:portOpenFailure:retry', { partIndex, attempt: attempt + 1, delay });
        return new Promise<void>((resolve) => setTimeout(resolve, delay)).then(() =>
          this.printPartWithPortRetry(printer, receipt, partIndex, attempt + 1)
        );
      }
      const message =
        typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: unknown }).message ?? '')
          : String(error);
      if (partIndex > 0) {
        // Earlier parts already printed → re-printing the whole job would duplicate them.
        throw new PrinterUncertainError(PrinterType.Star, message, error);
      }
      throw error; // nothing printed yet → safe for the queue to retry
    });
  }

  // --- TSP-143/TSP100 specific helpers ---
  public static isTsp100Series(model: string | undefined): boolean {
    if (!model) return false;
    const m = model.toLowerCase();
    return m.includes('tsp143') || m.includes('tsp100');
  }

  // --- TODO Need to remove ---
  // private transformTextForTsp100(text: string): string {
  //   // Replace lines that are only underscores with dashes of the same length
  //   return text
  //     .split('\n')
  //     .map((line) => (/^_+$/.test(line) ? '-'.repeat(line.length) : line))
  //     .join('\n');
  // }

  // --- TODO Need to remove ---
  // private preprocessReceiptForTsp100(receipt: any[]): any[] {
  //   return receipt.map((item) => {
  //     if (item && typeof item.text === 'string') {
  //       return { ...item, text: this.transformTextForTsp100(item.text) };
  //     }
  //     return item;
  //   });
  // }

  private splitByCut(receipt: any[]): any[][] {
    // Split receipt into parts by cut markers, preserving the cut item at the end of each part
    const result: any[][] = [];
    let current: any[] = [];
    for (let i = 0; i < receipt.length; i++) {
      const value = receipt[i];
      current.push(value);
      if (value && value.cut) {
        result.push(current);
        current = [];
      }
    }
    if (current.length) result.push(current);
    return result;
  }

  // Override: ensure TSP-143/TSP100 cuts between kitchen receipts and underline visibility
  public print(device: ReceiptPrinter, receipt: any[]): Promise<void> {
    this.logger.debug('StarPrinterService:print:override');
    const printerDeviceName = this.isAndroid ? device.deviceName : device.printerModel;
    const printer = new PrinterDto(
      device.deviceType,
      device.target,
      printerDeviceName,
      device.ipAddress,
      // keep mac lowercased for Star as in base class
      device.macAddress?.toLowerCase()
    );

    const modelId = device.printerModel || device.printerNameForModelPick || device.deviceName;
    const isTsp = StarPrinterService.isTsp100Series(modelId);
    // const prepared = isTsp ? this.preprocessReceiptForTsp100(receipt) : receipt;

    if (isTsp) {
      // Send each section as a separate job to guarantee hardware cut between parts.
      // Each part connects on its own, so a part that fails to open the LAN port (the
      // previous part's socket still in TIME_WAIT) is retried in place — see
      // printPartWithPortRetry — instead of failing and re-printing earlier parts.
      const parts = this.splitByCut(receipt);
      return parts.reduce((p, part, index) => {
        this.logger.debug('StarPrinterService:print:part', { index, partLength: part.length });
        return p.then(() => this.printPartWithPortRetry(printer, part, index));
      }, Promise.resolve());
    }

    // Non-TSP-100 series: default behavior, single job
    return this.printPartWithPortRetry(printer, receipt, 0);
  }

  // Command (CQS): start the native reader; events are consumed via `scannerEvents`.
  // No stop-before-start here: StarPrinterPlugin.stopBarcodeReader hangs when no reader is
  // running, which would block this start entirely. Duplicate native `observe('barcodeReader')`
  // listeners are instead prevented by the caller (BarcodeScannerService): start is issued once
  // per enable transition (idempotent), and a stop always runs on the disable transition.
  async startBarcodeReader(device: ReceiptPrinter): Promise<void> {
    const printerDeviceName = this.isAndroid ? device.deviceName : device.printerModel;
    const printer = new PrinterDto(device.deviceType, device.target, printerDeviceName, device.ipAddress, device.macAddress);
    StarPrinterPlugin.setBarcodeMessageListener({
      onMessage: (message) => this._scannerEvents.next(message),
      onError: (error) => this.logger.error(error, 'setBarcodeMessageListener', undefined, { printer }),
    });
    await StarPrinterPlugin.startBarcodeReader({ printer })
      .catch((error) => this.logger.error(error, 'startBarcodeReader', undefined, { printer }));
  }

  stopBarcodeReader(): Promise<void> {
    return StarPrinterPlugin.stopBarcodeReader();
  }
}
